import React from 'react';
import I18n from 'i18n-js';
import { first, get, isNull, isUndefined } from 'lodash';
import * as elog from '../elog';
import { ChildProps, MutationFn } from 'react-apollo';

import {
  CommonsOffersCreateOfferForm,
  CommonsOffersNewOfferForm,
  core_apimessages_BumpKey,
  core_apimessages_Error as RQLError,
  core_apimessages_Money as IMoney,
  core_apimessages_NegotiationParty as NegotiationParty,
  core_apimessages_ShippingMethod as IShippingMethod,
  Input_core_apimessages_BumpKey,
  Input_core_apimessages_CreateNegotiationRequest,
} from '../rql';
import ModalDialog from '../components/modal_dialog';
import { RCAlertBox } from '@reverbdotcom/cadence/components';
import OfferCreateFormFields from './offer_create_form_fields';
import LocationForm from './location_form';
import { connect } from '../connect';
import { connectCreateInitialOfferMutation, connectCreateOfferFormQuery } from './offer_operations';
import { IUserContext, withUserContext } from '../components/user_context_provider';
import { isUSSubregion } from './shipping_region';
import { canViewListingPriceRecommendations, isExperimentEnabled } from '../user_context_helpers';
import { BRAND_NEW, NON_FUNCTIONING } from '../condition_helpers';
import { convertCurrency, convertMoney } from '../convert_currency';
import experiments from '../experiments';
import { MParticleEventName, trackEvent } from '../elog/mparticle_tracker';
import { parseGraphqlErrors } from '../parse_graphql_errors';
import { formatNumber, parseAmount } from '../money';
import { shippingPriceDataForMethod } from '../components/listing_shipping_display';
import CreateOfferModalHeaderContent from './create_offer_modal_header_content';
import SanitizedRender from '../components/sanitized_render';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';

const RECOMMENDED_PRICE_DROP_MIN = 3; // percent
const RECOMMENDED_PRICE_DROP_MAX = 15; // percent

interface IOfferItem {
  listingId: string;
  shippingPrice?: IMoney;
  quantity: number;
  price: IMoney;
}

interface IExternalProps {
  listingId: string;
  recipientUuid?: string; // Must be passed if isSeller == true
  isSeller: boolean;
  onSuccess: (negotiation: CommonsOffersCreateOfferForm.Negotiations) => void;
  onError?: (error: RQLError[]) => void;
  onCancel?: () => void;
  isDisplayed: boolean;
  messageInput?: React.ReactNode;
  additionalMutationParams?: Partial<Input_core_apimessages_CreateNegotiationRequest>;
  bumpKey?: core_apimessages_BumpKey;
  headerTitle?: string;
  sidebarContent?: React.ReactNode;
  message?: string;
}

interface ApolloProps {
  createInitialOffer: MutationFn<CommonsOffersCreateOfferForm.Mutation, CommonsOffersCreateOfferForm.Variables>;
}

export type IProps = ChildProps<IExternalProps, CommonsOffersNewOfferForm.Query> & ApolloProps & IUserContext;

export interface IOfferFormState {
  price?: string;
  message?: string;
  shippingPrice?: string;
  quantity?: number;
}

export interface ISubmittedOffer {
  price: string;
  shippingPrice: string;
  totalPrice: string;
}

export function offerFormReducer(state, action): IOfferFormState {
  switch (action.type) {
    case 'setPrice':
      return { ...state, price: action.value };
    case 'setMessage':
      return { ...state, message: action.value };
    case 'setShippingPrice':
      return { ...state, shippingPrice: action.value };
    case 'setQuantity':
      return { ...state, quantity: parseInt(action.value, 10) };
    default:
      return { ...state };
  }
}

export const emptyOfferFormState = {
  price: '',
  message: '',
  shippingPrice: '',
  quantity: 1,
};

function BuyerLocation({ shipping, shop, countries, editing, setEditing, location, setLocation, refetch }) {
  return (
    <div className="mtb-1 bdt-1 bdb-1 bd-primary scaling-ptb-2">
      <LocationForm
        isEditing={editing}
        countries={countries}
        location={location}
        onLocationChange={(change) => {
          setLocation({
            ...location,
            ...change,
          });

          const shippingRegionChanging = change.shippingRegionCode && (change.shippingRegionCode !== location.shippingRegionCode);
          if (shippingRegionChanging) {
            refetch({ shippingRegionCode: change.shippingRegionCode, usePrimaryShippingAddress: false });
          }
        }}
        onEditLocation={setEditing}
      />

      {shipping.localPickupOnly && (
        <div className="mt-1">
          <RCAlertBox type="warning" small>
            {I18n.t('commons.offers.location.localPickupDetails', { location: shop.address.displayLocation })}
          </RCAlertBox>
        </div>
      )}

      {shipping.shippingPrices.length === 0 && !shipping.localPickupOnly && (
        <div className="mt-1">
          <RCAlertBox type="info" small>
            {I18n.t('commons.offers.location.noShippingCostSet')}
          </RCAlertBox>
        </div>
      )}
    </div>
  );
}


export function CreateOfferModal({
  listingId,
  isSeller,
  onSuccess,
  onCancel,
  isDisplayed,
  bumpKey,
  data,
  messageInput,
  user,
  recipientUuid,
  additionalMutationParams,
  onError,
  createInitialOffer,
  headerTitle,
  sidebarContent,
  message,
}: IProps) {
  const [formState, dispatch] = React.useReducer(offerFormReducer, emptyOfferFormState);
  const [isEditingLocation, setIsEditingLocation] = React.useState(true);
  const [location, setLocation] = React.useState({
    postalCode: '',
    shippingRegionCode: '',
  });
  const [isFormSaving, setIsFormSaving] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [submittedOffer, setSubmittedOffer] = React.useState<ISubmittedOffer>({ price: '', shippingPrice: '', totalPrice: '' });

  const { loading, error, listing } = data || {};

  React.useEffect(() => {
    if (loading || !listing) { return null; }

    dispatch({ type: 'setShippingPrice', value: standardShippingPrice() });

    const { shippingRegionCode, shippingAddress } = listing.shipping;

    setLocation({
      ...location,
      postalCode: shippingAddress?.postalCode || location.postalCode || '',
      shippingRegionCode: shippingRegionCode || '',
    });

    const missingRequiredLocation = !shippingAddress?.postalCode || !shippingRegionCode;
    setIsEditingLocation(!isSeller && missingRequiredLocation);
  }, [listing, loading]);

  // MessageForm passes its own message input component into this modal which has its own state, and we
  // need to keep the message portion of this component's state in sync with it
  React.useEffect(() => {
    if (isNull(message) || isUndefined(message)) { return; }

    dispatch({ type: 'setMessage', value: message });
  }, [message]);

  const exchangeRates = data?.exchangeRates?.rates || [];
  const shopCurrency = listing?.shop?.currency;
  const offerCurrency = isSeller ? shopCurrency : user?.currency;
  const buyerHasAtLeastOneAcceptedOffer = data && data.me?.offerCount?.total > 0;
  const shippingRegionCode = listing?.shipping?.shippingRegionCode;
  const showTaxAtCheckoutNotice = !isSeller && (!shippingRegionCode || isUSSubregion(shippingRegionCode));
  const preventLowballOffers = isExperimentEnabled(user, experiments.PREVENT_LOWBALL_OFFERS_RESTART) &&
    !!listing?.shop?.lowballOfferPercentage;
  const canViewPriceRecommendation = user && shopCurrency && canViewListingPriceRecommendations(user, shopCurrency);
  const isNewOrNonFunctioning = [BRAND_NEW, NON_FUNCTIONING].includes(listing?.condition?.conditionSlug);

  const useDesignChanges = !isSeller && !sidebarContent && isExperimentEnabled(user, experiments.OFFER_MODAL_DESIGN_UPDATES);
  const showPriceRecommendation = canViewPriceRecommendation &&
    listing?.price?.amountCents &&
    listing.canonicalProductId &&
    !isNewOrNonFunctioning &&
    listing.priceRecommendation?.priceHigh?.amountCents &&
    listing.price.amountCents <= listing.priceRecommendation.priceHigh.amountCents;
  const hasSubmittedOffer = !!submittedOffer?.totalPrice;

  function listingPriceInSellerCurrency() {
    return convertPriceInSellerCurrency(listing?.price);
  }

  function recommendedPriceInSellerCurrency() {
    return convertPriceInSellerCurrency(listing?.priceRecommendation?.priceMiddle);
  }

  function convertPriceInSellerCurrency(price: IMoney) {
    if (!shopCurrency || !price?.amountCents) { return null; }

    const { amountCents, currency } = price;
    return convertMoney({ amountCents, currency }, shopCurrency, exchangeRates);
  }

  function recommendedPriceDropPercent() {
    if (!canViewPriceRecommendation) { return null; }
    if (!listingPriceInSellerCurrency() || !recommendedPriceInSellerCurrency()) { return null; }
    if (isNewOrNonFunctioning) { return null; }

    const { amountCents: original } = listingPriceInSellerCurrency();
    const { amountCents: suggested } = recommendedPriceInSellerCurrency();

    const percentDrop = Math.round(((original - suggested) / original) * 100);

    if (percentDrop < RECOMMENDED_PRICE_DROP_MIN) { return null; }
    if (percentDrop > RECOMMENDED_PRICE_DROP_MAX) { return null; }

    return percentDrop;
  }

  function wouldBeAutoRejected() {
    if (!listing || !preventLowballOffers || !formState.price) {
      return false;
    }

    const originalPrice = parseFloat(listing.price.amount);
    const offerPrice = parseFloat(formState.price);
    const percentOff = Math.round((1 - (offerPrice / originalPrice)) * 100);

    return percentOff >= (100 - listing.shop.lowballOfferPercentage);
  }

  function calculateDiscountPercent() {
    return Math.round((1 - (parseFloat(formState.price) / parseFloat(listing.price.amount))) * 100);
  }

  const formatBumpKey = (key: core_apimessages_BumpKey): Input_core_apimessages_BumpKey => {
    if (!key?.key) { return null; }

    return { key: key.key };
  };

  function standardShippingPrice() {
    const standardShippingData = shippingPriceDataForMethod(listing, IShippingMethod.SHIPPED);
    if (standardShippingData?.carrierCalculated) { return undefined; }
    if (!standardShippingData?.rate?.amount) { return undefined; }
    return formatNumber(Number(standardShippingData.rate.amount));
  }

  function modalHeaderTitle() {
    if (hasSubmittedOffer) {
      return I18n.t('commons.offers.confirmation.modalHeader');
    }
    return headerTitle || I18n.t('commons.offers.action.create');
  }

  function submitButtonText() {
    if (hasSubmittedOffer) {
      return I18n.t('commons.offers.confirmation.confirmButton');
    }
    return I18n.t('commons.offers.submitOfferButton');
  }

  async function createNegotiation(): Promise<CommonsOffersCreateOfferForm.Negotiations> {
    const shopCurrencyOfferPrice = convertCurrency(
      offerCurrency,
      listing.shop.currency,
      exchangeRates,
      formState.price,
    );

    const offerItem: IOfferItem = {
      listingId: String(listingId),
      quantity: isSeller ? formState.quantity || 1 : 1,
      price: shopCurrencyOfferPrice,
    };

    if (isSeller) {
      offerItem.shippingPrice = parseAmount(
        formState.shippingPrice,
        listing.shop.currency,
      );
    }

    let shippingRegionCodeForBuyer = null;
    if (!isSeller) {
      shippingRegionCodeForBuyer = location.shippingRegionCode;
    }

    const input: Input_core_apimessages_CreateNegotiationRequest = {
      recipientUuid: recipientUuid,
      message: formState.message,
      offerItems: [offerItem],
      shippingRegionCode: shippingRegionCodeForBuyer,
      postalCode: location.postalCode,
      bumpKey: formatBumpKey(bumpKey),
      ...(additionalMutationParams || {}),
    };

    const result = await createInitialOffer({ variables: { input } });
    return get(result, 'data.createNegotiation.negotiations.0');
  }

  async function handleSubmit() {
    setIsEditingLocation(false);
    setIsFormSaving(true);
    setErrorMessage('');

    try {
      const response = await createNegotiation();

      trackEvent({
        eventName: MParticleEventName.SubmittedOffer,
        listingId: listingId,
        discountPercent: calculateDiscountPercent(),
        experiments: formatExperimentsForEventAttributes(user, [experiments.OFFER_MODAL_DESIGN_UPDATES]),
      });

      setIsFormSaving(false);

      if (useDesignChanges) {
        // Instead of running the onSuccess function which closes the modal and flashes a success message,
        // we flip to a confirmation message within the modal, so all we need to do is place the submitted offer
        // information into the state
        const { prices } = response.lastOffer;
        setSubmittedOffer({
          price: prices.price.display.display,
          shippingPrice: prices.shippingPrice.display.display,
          totalPrice: prices.totalPrice.display.display,
        });
      } else {
        onSuccess(response);
      }
    } catch (errors) {
      const parsedErrors = parseGraphqlErrors(errors);
      const { message } = first(parsedErrors);
      elog.error('offer.create', { errors }, message);
      setIsFormSaving(false);
      setErrorMessage(message);
      if (onError) {
        onError(parsedErrors);
      }
    }
  }

  return (
    <ModalDialog
      onRequestClose={onCancel}
      isOpen={isDisplayed}
      headerTitle={modalHeaderTitle()}
      saveButtonText={submitButtonText()}
      isDisabled={loading ||
        isFormSaving ||
        !formState.price ||
        (!isSeller && !location.shippingRegionCode) ||
        (!isSeller && !location.postalCode) ||
        wouldBeAutoRejected()}
      onSubmit={hasSubmittedOffer ? onCancel : handleSubmit} // in the new design there is no follow-up flash message, so we don't run the passed in onSuccess function
      sidebarContent={sidebarContent}
      hideFooterDismissButton={hasSubmittedOffer}
    >
      {error && (
        <RCAlertBox type="error">
          <p className="mb-0">
            {I18n.t('commons.offers.error')}
          </p>
        </RCAlertBox>
      )}

      {loading && (
        <div className="offers-form offers-form--loading">
          <div className="offers-form-placeholder" />
          <div className="offers-form-placeholder" />
          <div className="offers-form-placeholder" />
        </div>
      )}

      {!loading && !error && listing && (
        <>
          {useDesignChanges && (
            <CreateOfferModalHeaderContent
              listing={listing}
              isSeller={isSeller}
              countries={data?.countries?.countries || []}
              isEditingLocation={isEditingLocation}
              setIsEditingLocation={setIsEditingLocation}
              location={location}
              setLocation={setLocation}
              refetch={data.refetch}
              submittedOffer={hasSubmittedOffer ? submittedOffer : undefined}
            />
          )}

          {!useDesignChanges && (
            <div className="offers-form__header">
              <div className="offers-form__original-price">
                {I18n.t('commons.offers.originalPrice', { price: listing.price.display })}
              </div>
              {!isSeller && (
                <BuyerLocation
                  shipping={listing.shipping}
                  shop={listing.shop}
                  countries={data?.countries?.countries || []}
                  editing={isEditingLocation}
                  setEditing={setIsEditingLocation}
                  location={location}
                  setLocation={setLocation}
                  refetch={data.refetch}
                />
              )}
            </div>
          )}

          {!hasSubmittedOffer && (
            <OfferCreateFormFields
              listing={listing}
              exchangeRates={exchangeRates}
              currency={offerCurrency}
              offer={formState}
              onOfferChange={dispatch}
              buyerHasAtLeastOneAcceptedOffer={buyerHasAtLeastOneAcceptedOffer}
              party={isSeller ? NegotiationParty.SELLER : NegotiationParty.BUYER}
              // if we show editable location form, offer amount is no longer the first input, and we will instead autofocus
              // the shipping region selector (see components/offers/shipping_region_selector.tsx)
              autoFocus={!isEditingLocation}
              messageInput={messageInput}
              showTaxAtCheckoutNotices={showTaxAtCheckoutNotice}
              recommendedPriceDropPercent={recommendedPriceDropPercent()}
              preventLowballOffers={preventLowballOffers}
              wouldBeAutoRejected={wouldBeAutoRejected()}
              modalDialogVersion={true}
              useDesignChanges={useDesignChanges}
              priceRecommendation={showPriceRecommendation && listing.priceRecommendation}
            />
          )}

          {hasSubmittedOffer && (
            <div className="offers-form__confirmation">
              <h4>{I18n.t('commons.offers.confirmation.header')}</h4>
              <ul>
                <li>
                  <SanitizedRender
                    html={I18n.t('commons.offers.confirmation.line1')}
                  />
                </li>
                <li>
                  <SanitizedRender
                    html={I18n.t('commons.offers.confirmation.line2')}
                  />
                </li>
              </ul>
            </div>
          )}

          {errorMessage && (
            <div className="mt-1">
              <RCAlertBox data-error-message type="error" small>
                {errorMessage}
              </RCAlertBox>
            </div>
          )}
        </>
      )}
    </ModalDialog>
  );
}

export default connect<IExternalProps>([
  connectCreateOfferFormQuery,
  connectCreateInitialOfferMutation,
  withUserContext,
])(CreateOfferModal);
